import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface CustomTheme {
  id: string;
  name: string;
  primary: string;
  secondary: string;
  slug: string;
  mode: LightDarkMode;
}

type LightDarkMode = 'light' | 'dark';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private customThemes: CustomTheme[] = [
    // { id: 'violet-dream-1', name: 'Violet Dream', mode: 'dark', slug: 'violet-dream', primary: '#312057', secondary: '#8B5CF6' },
    // { id: 'violet-dream', name: 'Violet Dream', mode: 'light', slug: 'violet-dream', primary: '#312057', secondary: '#8B5CF6' },
    // { id: 'forest-mist', name: 'Forest Mist', mode: 'light', slug: 'forest-mist', primary: '#104533', secondary: '#10B981' },
    // { id: 'black-gray', name: 'Black Gray', mode: 'dark', slug: 'black-gray', primary: '#fafafa', secondary: '#000000' },
    // { id: 'white-black', name: 'White Black', mode: 'light', slug: 'white-black', primary: '#000000', secondary: '#EEEEEE' },
    { id: 'white-black', name: 'White Black', mode: 'light', slug: 'white-black', primary: '#EEEEEE', secondary: '#000000' },
    { id: 'crimson-tide', name: 'Crimson Tide', mode: 'light', slug: 'crimson-tide', primary: '#601b1b', secondary: '#EF4444' },
    // { id: 'desert-dusk', name: 'Desert Dusk', mode: 'dark', slug: 'desert-dusk', primary: '#ffe361', secondary: '#E11D48' },
    // { id: 'tropical-vibes', name: 'Tropical Vibes', mode: 'dark', slug: 'tropical-vibes', primary: '#13c7b6', secondary: '#007164' },
    
    
    // { id: 'forest-mist', name: 'Forest Mist', mode:'dark', slug: 'forest-mist', primary: '#10B981', secondary: '#D4D4D4' },
    // { id: 'crimson-tide', name: 'Crimson Tide', mode:'dark', slug: 'crimson-tide', primary: '#EF4444', secondary: '#FFD700' },
    // { id: 'desert-dusk', name: 'Desert Dusk', mode:'dark', slug: 'desert-dusk', primary: '#E11D48', secondary: '#F5E8B7' },
    // { id: 'tropical-vibes', name: 'Tropical Vibes', mode:'dark', slug: 'tropical-vibes', primary: '#14B8A6', secondary: '#F59E0B' },

    // { id: 'forest-mist', name: 'Forest Mist', mode:'light', slug: 'forest-mist', primary: '#10B981', secondary: '#D4D4D4' },
    // { id: 'crimson-tide', name: 'Crimson Tide', mode:'light', slug: 'crimson-tide', primary: '#EF4444', secondary: '#FFD700' },
    // { id: 'desert-dusk', name: 'Desert Dusk', mode:'light', slug: 'desert-dusk', primary: '#E11D48', secondary: '#F5E8B7' },
    // { id: 'tropical-vibes', name: 'Tropical Vibes', mode:'light', slug: 'tropical-vibes', primary: '#14B8A6', secondary: '#F59E0B' },
  ];

  private lightDarkMode: BehaviorSubject<LightDarkMode>;
  lightDarkMode$: Observable<LightDarkMode>;
  private currentCustomTheme: CustomTheme;

  constructor() {
    const savedCustomThemeId = localStorage.getItem('custom-theme');
    this.currentCustomTheme = this.customThemes.find(theme => theme.id === savedCustomThemeId) || this.customThemes[1];

    const savedMode = localStorage.getItem('light-dark-mode');
    const defaultMode: LightDarkMode = 'light';
    const initialMode: LightDarkMode = savedMode === 'dark' || savedMode === 'light' ? savedMode : defaultMode;

    this.lightDarkMode = new BehaviorSubject<LightDarkMode>(initialMode);
    this.lightDarkMode$ = this.lightDarkMode.asObservable();

    this.applyCustomTheme(this.currentCustomTheme);
    this.applyLightDarkMode(initialMode);
  }

  get themes(): CustomTheme[] {
    return this.customThemes;
  }

  get currentTheme(): CustomTheme {
    return this.currentCustomTheme;
  }

  get isDarkMode(): boolean {
    return this.lightDarkMode.value === 'dark';
  }

  setCustomTheme(themeId: string, mode?: LightDarkMode) {
    const theme = this.customThemes.find(t => t.id === themeId) || this.customThemes[0];
    this.currentCustomTheme = theme;
    this.applyCustomTheme(theme);
    localStorage.setItem('custom-theme', theme.id);
    if (mode) {
      this.lightDarkMode.next(mode);
      this.applyLightDarkMode(mode);
      localStorage.setItem('light-dark-mode', mode);
    }
  }

  toggleLightDarkMode() {
    const newMode: LightDarkMode = this.lightDarkMode.value === 'dark' ? 'light' : 'dark';
    this.lightDarkMode.next(newMode);
    this.applyLightDarkMode(newMode);
    localStorage.setItem('light-dark-mode', newMode);
  }

  private applyCustomTheme(theme: CustomTheme) {
    const root = document.documentElement;
    root.style.setProperty('--ion-color-primary', theme.primary);
    root.style.setProperty('--ion-color-primary-rgb', hexToRgb(theme.primary));
    root.style.setProperty('--ion-color-primary-contrast', getContrastColor(theme.primary));
    root.style.setProperty('--ion-color-secondary', theme.secondary);
    root.style.setProperty('--ion-color-secondary-rgb', hexToRgb(theme.secondary));
    root.style.setProperty('--ion-color-secondary-contrast', getContrastColor(theme.secondary));

    // Add these to override defaults for activated states
    const primaryShade = this.darkenColor(theme.primary, 20); // Implement darkenColor helper (see below)
    root.style.setProperty('--ion-color-primary-shade', primaryShade);
    root.style.setProperty('--background-activated', primaryShade);
    root.style.setProperty('--ripple-color', primaryShade);
  }

  // Helper to darken a hex color (e.g., by percentage)
  darkenColor(hex: string, percent: number): string {
    const num = parseInt(hex.replace('#', ''), 16);
    const amt = Math.round(2.55 * percent);
    const R = (num >> 16) + amt > 255 ? 255 : (num >> 16) + amt;
    const G = (num >> 8 & 0x00FF) + amt > 255 ? 255 : (num >> 8 & 0x00FF) + amt;
    const B = (num & 0x0000FF) + amt > 255 ? 255 : (num & 0x0000FF) + amt;
    return '#' + (0x1000000 + (R << 16) + (G << 8) + B).toString(16).slice(1);
  }

  private applyLightDarkMode(mode: LightDarkMode) {
    if (mode === 'dark') {
      document.body.setAttribute('data-theme', 'dark');
    } else {
      document.body.removeAttribute('data-theme');
    }
  }
}

// Helper functions
function hexToRgb(hex: string): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` : '0, 0, 0';
}

function getContrastColor(hex: string): string {
  const rgb = hexToRgb(hex).split(',').map(Number);
  const brightness = (rgb[0] * 299 + rgb[1] * 587 + rgb[2] * 114) / 1000;
  return brightness > 128 ? '#000000' : '#ffffff';
}